// AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { authAPI, setAuthToken, clearAuthToken } from '../utils/api';

const AuthContext = createContext();

export { AuthContext };

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [isInitialized, setIsInitialized] = useState(false);

  // 초기 토큰 설정 - 한 번만 실행
  useEffect(() => {
    
    const savedToken = localStorage.getItem('token');
    if (savedToken) {
      setToken(savedToken);
      setAuthToken(savedToken);
    }
    
    setIsInitialized(true);
  }, []); // 빈 배열로 한 번만 실행

  // 토큰 검증 및 사용자 정보 로드
  useEffect(() => {
    if (!isInitialized) return;

    const verifyToken = async () => {
      const currentToken = localStorage.getItem('token');
      
      if (!currentToken) {
        setUser(null);
        setLoading(false);
        return;
      }

      try {
        const response = await authAPI.verify();
        setUser(response.data.user);
        setLoading(false);
      } catch (error) {
        console.error(' 토큰 검증 실패:', error);
        // 토큰이 유효하지 않으면 완전히 초기화
        setUser(null);
        setToken(null);
        localStorage.removeItem('token'); // localStorage에서도 제거
        clearAuthToken();
        setLoading(false);
      }
    };

    verifyToken();
  }, [isInitialized]); // isInitialized가 변경될 때만 실행 (token 의존성 제거)

  const login = async (email, password) => {
    try {
      setLoading(true);
      
      const response = await authAPI.login({ email, password });
      
      const { token: newToken, user: userData } = response.data;
      
      
      // 토큰과 사용자 정보 설정
      setToken(newToken);
      setUser(userData);
      setAuthToken(newToken);
      
      toast.success(`안녕하세요, ${userData.username}님! `, { duration: 5000 });
      
      // 로딩 상태를 즉시 해제하여 리다이렉트가 원활하게 작동하도록 함
      setLoading(false);
      
      return { success: true, user: userData };
    } catch (error) {
      // 백엔드에서 제공하는 상세한 에러 정보 추출
      const errorData = error.response?.data || {};
      const message = errorData.error || '로그인에 실패했습니다.';
      const details = errorData.details;
      
      console.error('로그인 에러:', error);
      console.error('에러 응답:', error.response);
      
      // 개발 환경에서 상세 에러 정보 로깅
      if (process.env.NODE_ENV === 'development') {
        console.log('로그인 실패 상세 정보:', {
          message,
          details,
          status: error.response?.status,
          timestamp: new Date().toISOString()
        });
      }
      
      // Toast 메시지는 사용자 친화적으로 표시
      let toastMessage = message;
      if (details === 'INVALID_CREDENTIALS') {
        toastMessage = '이메일 또는 비밀번호가 올바르지 않습니다.';
      } else if (details === 'USER_NOT_FOUND') {
        toastMessage = '존재하지 않는 사용자입니다.';
      } else if (details === 'ACCOUNT_INACTIVE') {
        toastMessage = '비활성화된 계정입니다. 관리자에게 문의하세요.';
      }
      
      toast.error(toastMessage);
      
      // 상세한 에러 정보를 포함하여 반환
      return { 
        success: false, 
        error: message,
        details: details
      };
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData) => {
    try {
      setLoading(true);
      const response = await authAPI.register(userData);
      
      toast.success('회원가입이 완료되었습니다! ');
      
      return { success: true, user: response.data.user };
    } catch (error) {
      const message = error.response?.data?.error || '회원가입에 실패했습니다.';
      toast.error(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token'); // localStorage에서도 제거
    clearAuthToken();
    toast.success('안전하게 로그아웃되었습니다. ');
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await authAPI.updateProfile(profileData);
      setUser(response.data.user);
      toast.success('프로필이 업데이트되었습니다! ');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.error || '프로필 업데이트에 실패했습니다.';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      await authAPI.changePassword({ currentPassword, newPassword });
      toast.success('비밀번호가 성공적으로 변경되었습니다! ');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.error || '비밀번호 변경에 실패했습니다.';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  // 역할 확인 함수들
  const isStudent = () => {
    return user?.role === 'STUDENT';
  };

  const isTeacher = () => {
    return user?.role === 'TEACHER';
  };

  const isAdmin = () => {
    return user?.role === 'ADMIN';
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    isAuthenticated: !!user,
    isStudent,
    isTeacher,
    isAdmin,
    token
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
